import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, UGX, API_BASE } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, SectionCard, StatusPill, EmptyState, Chip, DoctorLabStatus } from "../components/Common";
import { toast } from "sonner";
import { Activity, Pill, FlaskConical, Image as ImageIcon, ClipboardList, FileText, AlertTriangle, Save, Printer, Signature, Plus, ArrowRight, Calendar, User, Phone, MapPin, Droplet, Zap, BedDouble, Search, X, Download, Radio, CheckCircle2 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { ImagingOrderModal } from "./Imaging";
import { printPatientChart } from "../lib/printPatientChart";

const TABS = [
  { key: "overview", label: "Overview", icon: Activity },
  { key: "visits",   label: "Previous visits", icon: Calendar },
  { key: "vitals",   label: "Vitals",   icon: Activity },
  { key: "meds",     label: "Medications", icon: Pill },
  { key: "mar",      label: "MAR", icon: Pill },
  { key: "io",       label: "I & O", icon: Activity },
  { key: "labs",     label: "Labs",     icon: FlaskConical },
  { key: "soap",     label: "SOAP Notes", icon: FileText },
  { key: "imaging",  label: "Imaging",  icon: ImageIcon },
  { key: "billing",  label: "Billing",  icon: ClipboardList },
];

export default function PatientChart() {
  const { patientId } = useParams();
  const { user, organization } = useAuth();
  const [patient, setPatient] = useState(null);
  const [tab, setTab] = useState("overview");
  const [vitals, setVitals] = useState([]);
  const [labs, setLabs] = useState([]);
  const [rx, setRx] = useState([]);
  const [notes, setNotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [imaging, setImaging] = useState([]);
  const [encounters, setEncounters] = useState([]);
  const [visits, setVisits] = useState([]);
  const [mar, setMar] = useState([]);
  const [printing, setPrinting] = useState(false);

  const [showOrderSet, setShowOrderSet] = useState(false);
  const [showAdmit, setShowAdmit] = useState(false);
  const [showImaging, setShowImaging] = useState(false);
  const [activeVisits, setActiveVisits] = useState([]);
  const [showComplete, setShowComplete] = useState(false);

  const load = async () => {
    try {
      const [{ data: p }, v, l, r, n, i, im, enc, av, vs] = await Promise.all([
        api.get(`/patients/${patientId}`),
        api.get(`/vitals?patient_id=${patientId}`),
        api.get(`/lab/orders?patient_id=${patientId}`),
        api.get(`/prescriptions?patient_id=${patientId}`),
        api.get(`/soap?patient_id=${patientId}`),
        api.get(`/invoices?patient_id=${patientId}`),
        api.get(`/imaging/orders?patient_id=${patientId}`),
        api.get(`/encounters?patient_id=${patientId}`),
        api.get(`/queue?patient_id=${patientId}`),
        api.get(`/visits?patient_id=${patientId}`),
      ]);
      setPatient(p);
      setVitals(v.data || []); setLabs(l.data || []); setRx(r.data || []);
      setNotes(n.data || []); setInvoices(i.data || []); setImaging(im.data || []);
      setEncounters(enc.data || []);
      setVisits(vs.data || []);
      const active = (av.data || []).filter((q) => q.status !== "Completed");
      setActiveVisits(active);
    } catch (e) { toast.error("Failed to load patient"); }
  };

  const completeVisit = async (queue_id) => {
    try {
      await api.post(`/queue/${queue_id}/status`, { status: "Completed" });
      toast.success("Visit marked as completed");
      setShowComplete(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to complete visit"); }
  };

  const onCompleteClick = () => {
    if (activeVisits.length === 0) return;
    if (activeVisits.length === 1) {
      if (window.confirm(`Mark this ${activeVisits[0].visit_type || "visit"} in ${activeVisits[0].department || "clinic"} as completed?\n\nAll clinical data stays on the patient chart. The row will move out of Today's Queue.`)) {
        completeVisit(activeVisits[0].queue_id);
      }
    } else {
      setShowComplete(true);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patientId]);

  if (!patient) return <div className="p-10 text-slate-500">Loading patient…</div>;

  const critical = labs.some((o) => (o.results || []).some((r) => r.flag === "critical"));

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="patient-chart">
      {/* Patient header */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        {critical && (
          <div className="mb-4 rounded-lg bg-[#fdecec] border border-[#f5c2c2] p-3 flex items-center gap-2 text-[#B71C1C] text-sm" data-testid="critical-banner">
            <AlertTriangle className="w-4 h-4" /> Critical lab result on file — review immediately.
          </div>
        )}
        <div className="flex items-start gap-5 flex-wrap">
          <div className="w-16 h-16 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xl font-semibold">
            {(patient.first_name?.[0] || "")}{(patient.last_name?.[0] || "")}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-[22px] font-semibold text-slate-900 tracking-tight">{patient.first_name} {patient.middle_name} {patient.last_name}</h1>
              <Chip tone="blue">{patient.ths_id}</Chip>
              <Chip tone="slate">{patient.gender}</Chip>
              {patient.blood_group && <Chip tone="red">Blood {patient.blood_group}</Chip>}
              {(patient.allergies || []).length > 0 && <Chip tone="amber">Allergies: {(patient.allergies || []).join(", ")}</Chip>}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600">
              <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> DOB {patient.date_of_birth || "—"}</span>
              <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {patient.phone_primary}</span>
              <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {patient.district || "—"}</span>
              <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> {patient.insurance_provider || "None"}</span>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button data-testid="apply-order-set" onClick={() => setShowOrderSet(true)} className="h-9 px-3 rounded-lg bg-[#e6f0fb] text-[#1565C0] text-sm flex items-center gap-1.5 hover:bg-[#d0e4fb] font-medium"><Zap className="w-3.5 h-3.5" /> Apply order set</button>
            <button data-testid="order-imaging-btn" onClick={() => setShowImaging(true)} className="h-9 px-3 rounded-lg bg-[#e6f0fb] text-[#1565C0] text-sm flex items-center gap-1.5 hover:bg-[#d0e4fb] font-medium"><Radio className="w-3.5 h-3.5" /> Order imaging</button>
            <button data-testid="admit-patient" onClick={() => setShowAdmit(true)} className="h-9 px-3 rounded-lg bg-[#e6f4ea] text-[#1B5E20] text-sm flex items-center gap-1.5 hover:bg-[#d3ecda] font-medium"><BedDouble className="w-3.5 h-3.5" /> Admit</button>
            <button
              data-testid="complete-visit"
              onClick={onCompleteClick}
              disabled={activeVisits.length === 0}
              title={activeVisits.length === 0 ? "No active visit in today's queue for this patient" : `Complete ${activeVisits.length > 1 ? `1 of ${activeVisits.length} active visits` : "this visit"}`}
              className={`h-9 px-3 rounded-lg text-sm flex items-center gap-1.5 font-semibold transition-colors ${activeVisits.length === 0 ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "bg-[#2E7D32] text-white hover:bg-[#1B5E20]"}`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Complete{activeVisits.length > 1 ? ` (${activeVisits.length})` : ""}
            </button>
            <button
              data-testid="print-chart"
              disabled={printing}
              onClick={async () => {
                setPrinting(true);
                try {
                  // Refresh MAR right before printing so admitted patients get the most current record
                  let latestMar = mar;
                  try { const { data } = await api.get(`/mar?patient_id=${patientId}`); latestMar = data || []; setMar(latestMar); } catch {}
                  printPatientChart({
                    patient,
                    vitals, prescriptions: rx, notes, labs, imaging, mar: latestMar,
                    generatedBy: user, organization,
                  });
                } catch (e) {
                  toast.error("Print failed. Please try again.");
                } finally {
                  setTimeout(() => setPrinting(false), 800);
                }
              }}
              className="h-9 px-3 rounded-lg border border-slate-200 text-sm flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-60"
            >
              <Printer className="w-3.5 h-3.5" /> {printing ? "Preparing…" : "Print"}
            </button>
          </div>
        </div>
      </div>

      {showOrderSet && <OrderSetModal patient={patient} onClose={() => setShowOrderSet(false)} onDone={() => { setShowOrderSet(false); load(); }} />}
      {showAdmit && <AdmitModal patient={patient} onClose={() => setShowAdmit(false)} onDone={() => { setShowAdmit(false); load(); }} />}
      {showImaging && <ImagingOrderModal prefillPatient={patient} onClose={() => setShowImaging(false)} onDone={() => { setShowImaging(false); load(); }} />}
      {showComplete && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={() => setShowComplete(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl border border-slate-200" onClick={(e) => e.stopPropagation()} data-testid="complete-visit-picker">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div className="font-semibold flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#2E7D32]" /> Which visit did you complete?</div>
              <button onClick={() => setShowComplete(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-2">
              <div className="text-xs text-slate-500 mb-2">This patient has {activeVisits.length} active visits in today's queue. Pick the one you just finished — the others stay in the queue.</div>
              {activeVisits.map((v) => (
                <button
                  key={v.queue_id}
                  data-testid={`complete-pick-${v.queue_id}`}
                  onClick={() => completeVisit(v.queue_id)}
                  className="w-full text-left rounded-lg border border-slate-200 hover:border-[#2E7D32]/60 hover:bg-emerald-50/40 p-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-800">#{v.queue_no} · {v.visit_type || "Outpatient"}</div>
                    <StatusPill status={v.status} />
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{v.department || "General"} · Registered {new Date(v.created_at).toLocaleTimeString(undefined,{hour:"2-digit",minute:"2-digit"})}{v.assigned_doctor_name ? ` · Dr. ${v.assigned_doctor_name}` : ""}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200 flex gap-1 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`chart-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm font-medium flex items-center gap-1.5 border-b-2 -mb-px ${tab === t.key ? "border-[#1976D2] text-[#1565C0]" : "border-transparent text-slate-500 hover:text-slate-800"}`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <OverviewTab patient={patient} vitals={vitals} labs={labs} rx={rx} notes={notes} invoices={invoices} encounters={encounters} onOpenVisits={() => setTab("visits")} />
      )}
      {tab === "visits" && <VisitsTab visits={visits} />}
      {tab === "vitals" && <VitalsTab patient={patient} vitals={vitals} onReload={load} />}
      {tab === "meds"   && <MedsTab patient={patient} rx={rx} onReload={load} />}
      {tab === "labs"   && <LabsTab patient={patient} labs={labs} onReload={load} />}
      {tab === "soap"   && <SOAPTab patient={patient} notes={notes} onReload={load} />}
      {tab === "mar"    && <MARTab patient={patient} />}
      {tab === "io"     && <IOTab patient={patient} />}
      {tab === "imaging" && (
        <SectionCard
          title="Imaging orders"
          actions={<button data-testid="chart-order-imaging" onClick={() => setShowImaging(true)} className="h-9 px-3 rounded-lg bg-[#1976D2] text-white text-xs font-medium flex items-center gap-1.5 hover:bg-[#1565C0]"><Radio className="w-3.5 h-3.5" /> Order imaging</button>}
        >
          {imaging.length === 0 ? (
            <EmptyState icon={ImageIcon} title="No imaging records" description="X-ray, CT, MRI, ultrasound, ECG and mammography studies will appear here once ordered." />
          ) : (
            <ul className="divide-y divide-slate-100">
              {imaging.map((o) => (
                <li key={o.order_id} className="p-4 flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold">{o.imaging_type}{o.body_part ? ` · ${o.body_part}` : ""}</span>
                      <Chip tone={o.priority === "STAT" ? "red" : o.priority === "Urgent" ? "amber" : "slate"}>{o.priority}</Chip>
                      <StatusPill status={o.status === "Reported" ? "Completed" : o.status === "In Progress" ? "In Progress" : "Pending"} />
                    </div>
                    <div className="text-xs text-slate-500 mt-1 tabular">{o.order_no} · {new Date(o.created_at).toLocaleString()} · {o.ordered_by_name}</div>
                    <div className="text-sm mt-2 text-slate-700">{o.clinical_indication}</div>
                    {o.findings && <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm"><span className="text-[11px] uppercase font-semibold text-slate-500">Findings</span><div className="whitespace-pre-wrap mt-1">{o.findings}</div></div>}
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <div className="text-sm tabular font-semibold">{UGX(o.price)}</div>
                    {o.status === "Reported" && (
                      <button data-testid={`chart-dl-report-${o.order_id}`} onClick={async () => {
                        const t = localStorage.getItem("ths_session_token");
                        const res = await fetch(`${API_BASE}/imaging/orders/${o.order_id}/report`, { credentials: "include", headers: t ? {Authorization: `Bearer ${t}`} : {} });
                        if (!res.ok) { toast.error("Cannot open report"); return; }
                        const blob = await res.blob(); const url = URL.createObjectURL(blob); window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000);
                      }} className="h-8 px-2.5 rounded-md bg-[#e6f4ea] text-[#1B5E20] text-xs font-medium flex items-center gap-1 hover:bg-[#d3ecda]">
                        <Download className="w-3.5 h-3.5" /> Open PDF
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      )}
      {tab === "billing" && <BillingTab patient={patient} invoices={invoices} onReload={load} />}
    </div>
  );
}

function OverviewTab({ patient, vitals, labs, rx, notes, invoices, encounters, onOpenVisits }) {
  const latestVital = vitals[0];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <SectionCard title="Latest vitals">
          {latestVital ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-5">
              {[
                ["Blood pressure", `${latestVital.systolic}/${latestVital.diastolic}`, "mmHg"],
                ["Pulse", latestVital.pulse, "bpm"],
                ["Temperature", latestVital.temperature, "°C"],
                ["SpO₂", latestVital.spo2, "%"],
              ].map(([l, v, u]) => (
                <div key={l}><div className="text-[11px] uppercase tracking-wider text-slate-500">{l}</div><div className="text-2xl font-semibold tabular mt-1">{v} <span className="text-sm text-slate-400 font-normal">{u}</span></div></div>
              ))}
            </div>
          ) : <EmptyState icon={Activity} title="No vitals recorded" />}
        </SectionCard>

        <SectionCard
          title="Previous visits"
          testId="overview-previous-visits"
          actions={encounters && encounters.length > 3 ? (
            <button data-testid="see-all-visits" onClick={onOpenVisits} className="text-xs text-[#1565C0] font-medium hover:underline">See all {encounters.length}</button>
          ) : null}
        >
          {!encounters || encounters.length === 0 ? (
            <EmptyState icon={Calendar} title="First visit on record" description="Previous encounters will appear here after their first consultation." />
          ) : (
            <ul className="divide-y divide-slate-100">
              {encounters.slice(0, 3).map((e) => (
                <li key={e.encounter_id} className="p-4" data-testid={`visit-${e.encounter_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-slate-800">{e.visit_type || "Consultation"}{e.department ? ` · ${e.department}` : ""}</div>
                    <div className="text-xs text-slate-500 tabular">{new Date(e.created_at).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric" })}</div>
                  </div>
                  {e.chief_complaint && <div className="text-xs text-slate-600 mt-1">{e.chief_complaint}</div>}
                  <div className="text-[11px] text-slate-400 mt-1 tabular">{e.encounter_no}</div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard title="Recent SOAP notes">
          {notes.length === 0 ? <EmptyState icon={FileText} title="No notes yet" description="Create a SOAP note in the Doctor workflow." /> : (
            <ul className="divide-y divide-slate-100">
              {notes.slice(0, 3).map((n) => (
                <li key={n.note_id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">{n.diagnosis || "Consultation"}</div>
                    <div className="text-xs text-slate-500">{new Date(n.created_at).toLocaleDateString()} · {n.author_name}</div>
                  </div>
                  {n.assessment && <div className="text-sm text-slate-600 mt-1 line-clamp-2">{n.assessment}</div>}
                  {n.signed && <div className="mt-1"><Chip tone="green">Signed</Chip></div>}
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>

      <div className="space-y-6">
        <SectionCard title="Active medications">
          {rx.length === 0 ? <EmptyState icon={Pill} title="No prescriptions" /> : (
            <ul className="divide-y divide-slate-100">
              {rx.map((r) => (
                <li key={r.rx_id} className="p-4">
                  <div className="text-sm font-medium">{(r.items || []).map((i) => i.drug).join(", ")}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{r.rx_no} · <StatusPill status={r.status} /></div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
        <SectionCard title="Recent labs">
          {labs.length === 0 ? <EmptyState icon={FlaskConical} title="No lab orders" /> : (
            <ul className="divide-y divide-slate-100">
              {labs.slice(0, 4).map((l) => (
                <li key={l.order_id} className="p-4">
                  <div className="text-sm font-medium">{(l.tests || []).join(", ")}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{l.order_no} · <DoctorLabStatus status={l.status} /></div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
        <SectionCard title="Outstanding bills">
          {invoices.filter((i) => i.status !== "Paid").length === 0
            ? <EmptyState icon={ClipboardList} title="No outstanding bills" />
            : invoices.filter((i) => i.status !== "Paid").map((i) => (
              <div key={i.invoice_id} className="p-4 border-b border-slate-100 last:border-0">
                <div className="flex items-center justify-between">
                  <div><div className="text-sm font-medium">{i.invoice_no}</div><div className="text-xs text-slate-500">{new Date(i.created_at).toLocaleDateString()}</div></div>
                  <div className="text-right"><div className="text-sm font-semibold tabular">{UGX(i.balance)}</div><StatusPill status={i.status} /></div>
                </div>
              </div>
            ))}
        </SectionCard>
      </div>
    </div>
  );
}

function VitalsTab({ patient, vitals, onReload }) {
  const [form, setForm] = useState({ systolic: 120, diastolic: 80, pulse: 72, temperature: 36.7, spo2: 98, respiratory_rate: 16, pain_score: 0 });
  const submit = async () => {
    try { await api.post("/vitals", { patient_id: patient.patient_id, ...form }); toast.success("Vitals recorded"); onReload(); }
    catch { toast.error("Failed to save"); }
  };
  const trend = [...vitals].reverse().map((v) => ({ date: new Date(v.created_at).toLocaleDateString(), sys: v.systolic, dia: v.diastolic, pulse: v.pulse }));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <SectionCard title="Record vitals">
        <div className="p-4 grid grid-cols-2 gap-3">
          {[
            ["systolic","Systolic","mmHg"],["diastolic","Diastolic","mmHg"],
            ["pulse","Pulse","bpm"],["temperature","Temp","°C"],
            ["spo2","SpO₂","%"],["respiratory_rate","RR","/min"],
            ["pain_score","Pain","0-10"],
          ].map(([k, l, u]) => (
            <label key={k}><span className="text-[11px] uppercase text-slate-500 tracking-wider">{l}</span>
              <input data-testid={`vital-${k}`} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: Number(e.target.value) }))} className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm tabular focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40" />
              <div className="text-[10px] text-slate-400 mt-0.5">{u}</div>
            </label>
          ))}
        </div>
        <div className="p-4 border-t border-slate-200 flex justify-end"><button data-testid="save-vitals" onClick={submit} className="px-4 h-9 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold flex items-center gap-1.5"><Save className="w-4 h-4" /> Save vitals</button></div>
      </SectionCard>

      <div className="lg:col-span-2 space-y-6">
        <SectionCard title="Blood pressure & pulse trend">
          <div className="p-4 h-64">
            {trend.length === 0 ? <EmptyState icon={Activity} title="No vitals yet" /> : (
              <ResponsiveContainer>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="sys" stroke="#1976D2" name="Systolic" strokeWidth={2} />
                  <Line type="monotone" dataKey="dia" stroke="#2E7D32" name="Diastolic" strokeWidth={2} />
                  <Line type="monotone" dataKey="pulse" stroke="#D32F2F" name="Pulse" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </SectionCard>

        <SectionCard title="History">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Date</th><th className="text-left px-4 py-2.5">BP</th><th className="text-left px-4 py-2.5">Pulse</th>
                  <th className="text-left px-4 py-2.5">Temp</th><th className="text-left px-4 py-2.5">SpO₂</th><th className="text-left px-4 py-2.5">By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {vitals.map((v) => (
                  <tr key={v.vital_id}>
                    <td className="px-4 py-2.5">{new Date(v.created_at).toLocaleString()}</td>
                    <td className="px-4 py-2.5 tabular">{v.systolic}/{v.diastolic}</td>
                    <td className="px-4 py-2.5 tabular">{v.pulse}</td>
                    <td className="px-4 py-2.5 tabular">{v.temperature}</td>
                    <td className="px-4 py-2.5 tabular">{v.spo2}</td>
                    <td className="px-4 py-2.5">{v.recorded_by_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

function MedsTab({ patient, rx, onReload }) {
  const [drug, setDrug] = useState(""); const [dose, setDose] = useState(""); const [freq, setFreq] = useState("TDS"); const [dur, setDur] = useState("5 days"); const [qty, setQty] = useState(15);
  const [warnings, setWarnings] = useState({ interactions: [], allergies: [] });
  const [checking, setChecking] = useState(false);

  const check = async (name) => {
    if (!name) return;
    setChecking(true);
    try {
      const { data } = await api.post("/drug-interactions/check", { patient_id: patient.patient_id, drugs: [name] });
      setWarnings({ interactions: data.interactions || [], allergies: data.allergies || [] });
    } finally { setChecking(false); }
  };

  const submit = async () => {
    if (!drug) return toast.error("Drug required");
    if (warnings.allergies.length > 0 && !window.confirm(`Allergy warning: patient is allergic to ${warnings.allergies.map(a=>a.allergy).join(', ')}. Prescribe anyway?`)) return;
    if (warnings.interactions.some(w => w.severity === "major") && !window.confirm(`MAJOR interaction detected. Prescribe anyway?`)) return;
    try {
      await api.post("/prescriptions", { patient_id: patient.patient_id, items: [{ drug, dose, frequency: freq, duration: dur, qty: Number(qty) }] });
      toast.success("Prescription created");
      setDrug(""); setWarnings({ interactions: [], allergies: [] }); onReload();
    } catch { toast.error("Failed to create Rx"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <SectionCard title="Prescribe medication">
        <div className="p-4 space-y-3">
          <input data-testid="rx-drug" value={drug} onChange={(e) => { setDrug(e.target.value); check(e.target.value); }} placeholder="Drug (e.g. Amoxicillin 500mg)" className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
          {(warnings.allergies.length > 0 || warnings.interactions.length > 0) && (
            <div className="space-y-2" data-testid="rx-warnings">
              {warnings.allergies.map((a,i)=>(<div key={i} className="rounded-lg bg-[#fdecec] border border-[#f5c2c2] text-[#B71C1C] text-xs p-2 flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5" /><span><strong>ALLERGY:</strong> patient allergic to {a.allergy} — matches {a.drug}</span></div>))}
              {warnings.interactions.map((w,i)=>(<div key={i} className={`rounded-lg text-xs p-2 flex items-start gap-1.5 ${w.severity==="major"?"bg-[#fdecec] border border-[#f5c2c2] text-[#B71C1C]":"bg-[#fef4e2] border border-[#f4d9a0] text-[#915907]"}`}><AlertTriangle className="w-3.5 h-3.5 mt-0.5" /><span><strong>{w.severity.toUpperCase()}:</strong> {w.drug_a} ⚠ {w.drug_b}. {w.note}</span></div>))}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <input value={dose} onChange={(e) => setDose(e.target.value)} placeholder="Dose" className="h-9 px-3 rounded-lg border border-slate-200 text-sm" />
            <input value={freq} onChange={(e) => setFreq(e.target.value)} placeholder="Frequency" className="h-9 px-3 rounded-lg border border-slate-200 text-sm" />
            <input value={dur} onChange={(e) => setDur(e.target.value)} placeholder="Duration" className="h-9 px-3 rounded-lg border border-slate-200 text-sm" />
            <input value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Qty" className="h-9 px-3 rounded-lg border border-slate-200 text-sm tabular" />
          </div>
          <button data-testid="rx-submit" onClick={submit} className="w-full h-9 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold">Send to pharmacy queue</button>
          <div className="text-[11px] text-slate-500">Prescription is e-signed as {patient.first_name ? "" : ""}<strong>current user</strong> and appears in the Pharmacy dispensing queue immediately.</div>
        </div>
      </SectionCard>
      <div className="lg:col-span-2">
        <SectionCard title="Prescription history">
          {rx.length === 0 ? <EmptyState icon={Pill} title="No prescriptions" /> : (
            <ul className="divide-y divide-slate-100">
              {rx.map((r) => (
                <li key={r.rx_id} className="p-4">
                  <div className="flex items-center justify-between"><div><div className="text-sm font-semibold">{r.rx_no}</div><div className="text-xs text-slate-500">{new Date(r.created_at).toLocaleString()} · Prescriber: <strong>{r.prescribed_by_name}</strong></div></div><StatusPill status={r.status} /></div>
                  <ul className="mt-2 text-sm text-slate-700 list-disc list-inside">{(r.items||[]).map((i,idx)=><li key={idx}>{i.drug} — {i.dose} {i.frequency} × {i.duration} (Qty {i.qty})</li>)}</ul>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

function LabsTab({ patient, labs, onReload }) {
  const [tests, setTests] = useState("CBC, Malaria RDT");
  const [priority, setPriority] = useState("Routine");
  const [prices, setPrices] = useState({ tests: [], default_price: 15000 });
  const [totalMode, setTotalMode] = useState("auto"); // auto | override
  const [customTotal, setCustomTotal] = useState(0);

  useEffect(() => {
    api.get("/lab/test-prices").then((r) => setPrices(r.data || { tests: [], default_price: 15000 })).catch(() => {});
  }, []);

  const testList = tests.split(",").map((s) => s.trim()).filter(Boolean);
  const priceFor = (name) => {
    const q = name.trim().toLowerCase();
    const hit = (prices.tests || []).find((t) => t.name.toLowerCase() === q || (t.aliases || []).some((a) => a.toLowerCase() === q));
    return hit ? Number(hit.price) : Number(prices.default_price || 15000);
  };
  const perTest = testList.map((t) => ({ name: t, price: priceFor(t) }));
  const computed = perTest.reduce((s, x) => s + x.price, 0);
  const effectiveTotal = totalMode === "override" ? Math.max(0, Number(customTotal) || 0) : computed;

  const submit = async () => {
    if (!testList.length) return toast.error("Enter at least one test");
    let allowZero = false;
    if (effectiveTotal <= 0) {
      if (!window.confirm("No charge entered for this order — continue without billing?")) return;
      allowZero = true;
    }
    try {
      const { data } = await api.post("/lab/orders", {
        patient_id: patient.patient_id,
        tests: testList,
        priority,
        total_override: totalMode === "override" ? Number(effectiveTotal) : null,
        allow_zero: allowZero,
      });
      const inv = data?.invoice;
      toast.success(inv ? `Lab order sent · billed ${UGX(inv.total)} — ${inv.invoice_no}` : "Lab order sent");
      onReload();
    } catch { toast.error("Failed"); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <SectionCard title="Order tests">
        <div className="p-4 space-y-3">
          <textarea data-testid="lab-tests" value={tests} onChange={(e) => setTests(e.target.value)} placeholder="Tests (comma-separated)" rows={3} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          <select data-testid="lab-priority" value={priority} onChange={(e) => setPriority(e.target.value)} className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
            <option>Routine</option><option>Urgent</option><option>STAT</option>
          </select>
          {perTest.length > 0 && (
            <div className="rounded-lg border border-[#e3ebf5] bg-[#f6f9ff] p-3 text-xs" data-testid="lab-cost-breakdown">
              <div className="text-[10px] uppercase tracking-wider text-[#1565C0] font-semibold mb-1">Cost breakdown</div>
              <ul className="space-y-0.5 mb-2">
                {perTest.map((x, i) => (
                  <li key={i} className="flex justify-between tabular"><span>{x.name}</span><span>{UGX(x.price)}</span></li>
                ))}
              </ul>
              <div className="border-t border-slate-200 pt-1.5 space-y-1">
                <label className="flex items-center gap-1.5">
                  <input type="radio" data-testid="lab-total-auto" checked={totalMode === "auto"} onChange={() => setTotalMode("auto")} className="accent-[#1976D2]" />
                  Bill computed total <span className="tabular font-semibold ml-auto">{UGX(computed)}</span>
                </label>
                <label className="flex items-center gap-1.5">
                  <input type="radio" data-testid="lab-total-override" checked={totalMode === "override"} onChange={() => { setTotalMode("override"); setCustomTotal(computed); }} className="accent-[#1976D2]" />
                  Override <input data-testid="lab-total-custom" type="number" min={0} step={500} disabled={totalMode !== "override"} value={customTotal} onChange={(e) => setCustomTotal(e.target.value)} className="ml-auto w-24 h-7 px-2 rounded border border-slate-200 text-right tabular text-xs disabled:bg-slate-100" />
                </label>
              </div>
            </div>
          )}
          <button data-testid="lab-submit" onClick={submit} className="w-full h-9 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold">Send to laboratory · bill {UGX(effectiveTotal)}</button>
        </div>
      </SectionCard>
      <div className="lg:col-span-2">
        <SectionCard title="Lab results">
          {labs.length === 0 ? <EmptyState icon={FlaskConical} title="No labs" /> : (
            <ul className="divide-y divide-slate-100">
              {labs.map((l) => (
                <li key={l.order_id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold">{l.order_no}</div>
                      <div className="text-xs text-slate-500">{(l.tests || []).join(", ")} · {new Date(l.created_at).toLocaleString()}</div>
                    </div>
                    <DoctorLabStatus status={l.status} />
                  </div>
                  {(l.results || []).length > 0 && (
                    <table className="mt-2 w-full text-sm border border-slate-200 rounded-md overflow-hidden">
                      <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                        <tr><th className="text-left px-3 py-1.5">Test</th><th className="text-left px-3 py-1.5">Value</th><th className="text-left px-3 py-1.5">Unit</th><th className="text-left px-3 py-1.5">Reference</th><th className="text-left px-3 py-1.5">Flag</th></tr>
                      </thead>
                      <tbody>
                        {l.results.map((r, idx) => (
                          <tr key={idx} className="border-t border-slate-100">
                            <td className="px-3 py-1.5">{r.test}</td>
                            <td className="px-3 py-1.5 tabular font-semibold">{r.value}</td>
                            <td className="px-3 py-1.5 tabular">{r.unit}</td>
                            <td className="px-3 py-1.5 text-slate-500 tabular">{r.reference}</td>
                            <td className="px-3 py-1.5"><StatusPill status={r.flag} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

function DiagnosesPanel({ patient, diagnoses, onReload }) {
  const empty = { date_diagnosed: "", description: "", icd_code: "", icd_name: "", type: "Primary", status: "Active" };
  const [f, setF] = React.useState(empty);
  const [icdQ, setIcdQ] = React.useState("");
  const [icdRes, setIcdRes] = React.useState([]);
  const [saving, setSaving] = React.useState(false);

  const searchICD = async (v) => {
    setIcdQ(v);
    if (!v || v.length < 2) { setIcdRes([]); return; }
    try { const { data } = await api.get(`/icd/search?q=${encodeURIComponent(v)}`); setIcdRes(data || []); } catch { setIcdRes([]); }
  };

  const save = async () => {
    if (!f.date_diagnosed.trim() || !f.description.trim()) {
      toast.error("Date diagnosed and diagnosis description are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/diagnoses", { patient_id: patient.patient_id, ...f });
      toast.success("Diagnosis recorded");
      setF(empty); setIcdQ(""); setIcdRes([]);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save diagnosis");
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this diagnosis?")) return;
    try { await api.delete(`/diagnoses/${id}`); toast.success("Diagnosis deleted"); onReload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <SectionCard title={`Diagnoses · ${diagnoses.length}`} testId="diagnoses-panel">
      <div className="p-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Date diagnosed</span>
            <input data-testid="dx-date" type="date" value={f.date_diagnosed} onChange={(e)=>setF({...f, date_diagnosed:e.target.value})} className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
          </label>
          <label className="block md:col-span-2">
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Diagnosis description</span>
            <input data-testid="dx-description" value={f.description} onChange={(e)=>setF({...f, description:e.target.value})} placeholder="e.g. Type 2 Diabetes Mellitus" className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Type</span>
            <select data-testid="dx-type" value={f.type} onChange={(e)=>setF({...f, type:e.target.value})} className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
              {["Primary","Secondary","Provisional","Differential","Final"].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Status</span>
            <select data-testid="dx-status" value={f.status} onChange={(e)=>setF({...f, status:e.target.value})} className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
              {["Active","Resolved","Chronic","Ruled out"].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="block relative">
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">ICD-10 (optional)</span>
            <div className="relative mt-1">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="dx-icd-search" value={f.icd_code ? `${f.icd_code} · ${f.icd_name}` : icdQ} onChange={(e)=>{ setF({...f, icd_code:"", icd_name:""}); searchICD(e.target.value); }} placeholder="Search ICD-10…" className="w-full h-9 pl-8 pr-3 rounded-lg border border-slate-200 text-sm" />
              {icdRes.length > 0 && !f.icd_code && (
                <div className="absolute z-20 top-full mt-1 left-0 right-0 max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg">
                  {icdRes.map(c => (
                    <button key={c.code} data-testid={`dx-icd-${c.code}`} onClick={()=>{ setF({...f, icd_code:c.code, icd_name:c.name}); setIcdRes([]); setIcdQ(""); }} className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0">
                      <div className="text-xs font-semibold tabular text-[#1565C0]">{c.code}</div>
                      <div className="text-xs text-slate-600">{c.name}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </label>
        </div>
        <div className="flex justify-end">
          <button data-testid="dx-save" disabled={saving} onClick={save} className="px-4 h-9 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] disabled:opacity-60 text-white text-sm font-semibold flex items-center gap-1.5"><Plus className="w-4 h-4" /> {saving ? "Saving…" : "Add diagnosis"}</button>
        </div>

        {diagnoses.length === 0 ? (
          <div className="text-sm text-slate-500 py-4 text-center border-t border-slate-100">No diagnosis recorded</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm mt-2">
              <thead className="text-[11px] uppercase text-slate-500 border-b border-slate-200">
                <tr><th className="text-left py-2">Date</th><th className="text-left">Diagnosis</th><th className="text-left">ICD-10</th><th className="text-left">Type</th><th className="text-left">Status</th><th className="text-left">Diagnosed by</th><th></th></tr>
              </thead>
              <tbody className="divide-y">
                {diagnoses.map(d => (
                  <tr key={d.diagnosis_id} data-testid={`dx-row-${d.diagnosis_id}`}>
                    <td className="py-2 tabular text-slate-600 whitespace-nowrap">{d.date_diagnosed}</td>
                    <td>{d.description}</td>
                    <td className="tabular text-xs text-[#1565C0]">{d.icd_code ? `${d.icd_code}${d.icd_name ? " — "+d.icd_name : ""}` : "—"}</td>
                    <td className="text-xs">{d.type}</td>
                    <td className="text-xs">{d.status}</td>
                    <td className="text-xs text-slate-600">{d.diagnosed_by_name}</td>
                    <td><button onClick={()=>remove(d.diagnosis_id)} className="text-xs text-slate-400 hover:text-[#B71C1C]"><X className="w-3.5 h-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SectionCard>
  );
}

function SOAPTab({ patient, notes, onReload }) {
  const draftKey = `ths_soap_draft_${patient.patient_id}`;
  const emptyNote = { subjective: "", objective: "", assessment: "", plan: "", diagnosis: "", icd_code: "", icd_name: "" };
  const [note, setNote] = useState(() => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) return { ...emptyNote, ...JSON.parse(raw) };
    } catch (e) { console.debug("failed to parse SOAP draft from storage", e); }
    return emptyNote;
  });
  const [icdQ, setIcdQ] = useState("");
  const [icdRes, setIcdRes] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editBuf, setEditBuf] = useState(null);

  // Auto-save the draft on every change so it survives tab-switch and refresh
  useEffect(() => {
    const hasContent = Object.values(note).some((v) => (v || "").toString().trim().length > 0);
    if (hasContent) localStorage.setItem(draftKey, JSON.stringify(note));
    else localStorage.removeItem(draftKey);
  }, [note, draftKey]);

  const searchICD = async (v) => { setIcdQ(v); if (v.length<2){setIcdRes([]);return;} const {data} = await api.get(`/icd/search?q=${encodeURIComponent(v)}`); setIcdRes(data||[]); };
  const save = async (signed) => {
    const hasContent = Object.values(note).some((v) => (v || "").toString().trim().length > 0);
    if (!hasContent) { toast.error("Note is empty"); return; }
    try {
      await api.post("/soap", { patient_id: patient.patient_id, ...note, signed });
      toast.success(signed ? "Note signed & locked" : "Draft saved to chart");
      localStorage.removeItem(draftKey);
      setNote(emptyNote);
      onReload();
    } catch { toast.error("Failed to save note"); }
  };
  const clearDraft = () => {
    if (!window.confirm("Discard this unsaved draft?")) return;
    localStorage.removeItem(draftKey);
    setNote(emptyNote);
    toast.info("Draft cleared");
  };
  const draftDirty = Object.values(note).some((v) => (v || "").toString().trim().length > 0);

  const startEdit = (n) => {
    setEditingId(n.note_id);
    setEditBuf({
      subjective: n.subjective || "", objective: n.objective || "",
      assessment: n.assessment || "", plan: n.plan || "",
      diagnosis: n.diagnosis || "", icd_code: n.icd_code || "", icd_name: n.icd_name || "",
    });
    setExpanded(n.note_id);
  };
  const cancelEdit = () => { setEditingId(null); setEditBuf(null); };
  const saveEdit = async (note_id) => {
    try {
      await api.post(`/soap/${note_id}/edit`, editBuf);
      toast.success("Draft updated");
      setEditingId(null); setEditBuf(null);
      onReload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save edits"); }
  };
  const signNote = async (note_id) => {
    if (!window.confirm("Signing locks this note — you won't be able to edit it further. Continue?")) return;
    try {
      await api.post(`/soap/${note_id}/sign`);
      toast.success("Note signed & locked");
      setEditingId(null); setEditBuf(null);
      onReload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to sign note"); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <SectionCard title="New consultation note (SOAP)">
          <div className="p-4 space-y-3">
            {[
              ["subjective","Subjective — patient's report"],
              ["objective","Objective — exam & findings"],
              ["assessment","Assessment"],
              ["plan","Plan"],
            ].map(([k, l]) => (
              <div key={k}>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{l}</div>
                <textarea data-testid={`soap-${k}`} value={note[k]} onChange={(e) => setNote({ ...note, [k]: e.target.value })} rows={3} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              </div>
            ))}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Diagnosis</div>
                <input data-testid="soap-diagnosis" value={note.diagnosis} onChange={(e) => setNote({ ...note, diagnosis: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">ICD-10 code</div>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input data-testid="soap-icd-search" value={note.icd_code ? `${note.icd_code} · ${note.icd_name}` : icdQ} onChange={(e)=>{ setNote({...note,icd_code:"",icd_name:""}); searchICD(e.target.value); }} placeholder="Search ICD-10…" className="w-full h-9 pl-8 pr-3 rounded-lg border border-slate-200 text-sm" />
                  {icdRes.length>0 && !note.icd_code && (
                    <div className="absolute z-10 top-full mt-1 left-0 right-0 max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg">
                      {icdRes.map(c => (
                        <button key={c.code} data-testid={`soap-icd-${c.code}`} onClick={()=>{ setNote({...note,icd_code:c.code,icd_name:c.name}); setIcdRes([]); setIcdQ(""); }} className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0">
                          <div className="text-xs font-semibold tabular text-[#1565C0]">{c.code}</div>
                          <div className="text-xs text-slate-600">{c.name}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] text-slate-500" data-testid="soap-draft-hint">
                {draftDirty ? "Draft auto-saved to this browser — safe to switch tabs." : "Fields save to the chart when you click Save draft or Sign & lock."}
              </div>
              <div className="flex gap-2">
                {draftDirty && (
                  <button data-testid="soap-clear-draft" onClick={clearDraft} className="px-3 h-9 rounded-lg text-slate-500 hover:bg-slate-50 text-sm font-medium">Discard</button>
                )}
                <button data-testid="soap-save-draft" onClick={() => save(false)} className="px-4 h-9 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm font-medium flex items-center gap-1.5"><Save className="w-4 h-4" /> Save draft</button>
                <button data-testid="soap-sign" onClick={() => save(true)} className="px-4 h-9 rounded-lg bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-sm font-semibold flex items-center gap-1.5"><Signature className="w-4 h-4" /> Sign & lock</button>
              </div>
            </div>
          </div>
        </SectionCard>
      </div>
      <SectionCard title="Signed notes" testId="soap-signed-list">
        {notes.length === 0 ? <EmptyState icon={FileText} title="No notes yet" /> : (
          <ul className="divide-y divide-slate-100">
            {notes.map((n) => {
              const isOpen = expanded === n.note_id;
              const hasBody = n.subjective || n.objective || n.assessment || n.plan;
              const isEditing = editingId === n.note_id;
              return (
                <li key={n.note_id} className="p-4" data-testid={`signed-note-${n.note_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium min-w-0 truncate">{n.diagnosis || "Consultation"}</div>
                    {n.signed ? <Chip tone="green" data-testid={`badge-signed-${n.note_id}`}>Signed</Chip> : <Chip tone="amber" data-testid={`badge-draft-${n.note_id}`}>Draft</Chip>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {new Date(n.created_at).toLocaleString()} · {n.author_name}
                    {n.signed && n.signed_at && (
                      <span className="ml-1 text-[#1B5E20]">· Signed {new Date(n.signed_at).toLocaleString()}{n.signed_by_name ? ` by ${n.signed_by_name}` : ""}</span>
                    )}
                  </div>
                  {n.icd_code && <div className="text-xs text-[#1565C0] mt-1 tabular">ICD-10: {n.icd_code} — {n.icd_name}</div>}
                  {n.assessment && !isOpen && !isEditing && <div className="text-sm text-slate-700 mt-1.5 line-clamp-2">{n.assessment}</div>}
                  {isOpen && !isEditing && hasBody && (
                    <div className="mt-2 space-y-2 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
                      {[["Subjective", n.subjective], ["Objective", n.objective], ["Assessment", n.assessment], ["Plan", n.plan]].map(([label, val]) => val ? (
                        <div key={label}>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
                          <div className="text-sm text-slate-700 whitespace-pre-wrap">{val}</div>
                        </div>
                      ) : null)}
                    </div>
                  )}
                  {isEditing && editBuf && (
                    <div className="mt-2 space-y-2 rounded-lg border border-amber-200 bg-amber-50/50 p-3" data-testid={`edit-form-${n.note_id}`}>
                      <div className="text-[11px] text-amber-700 font-semibold uppercase tracking-wider">Editing draft — Sign to lock</div>
                      {[["Subjective", "subjective"], ["Objective", "objective"], ["Assessment", "assessment"], ["Plan", "plan"]].map(([label, key]) => (
                        <div key={key}>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
                          <textarea
                            data-testid={`edit-${key}-${n.note_id}`}
                            value={editBuf[key] || ""}
                            onChange={(e) => setEditBuf({ ...editBuf, [key]: e.target.value })}
                            className="w-full mt-1 h-16 p-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 focus:border-[#1976D2]"
                          />
                        </div>
                      ))}
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Diagnosis</div>
                        <input
                          data-testid={`edit-diagnosis-${n.note_id}`}
                          value={editBuf.diagnosis || ""}
                          onChange={(e) => setEditBuf({ ...editBuf, diagnosis: e.target.value })}
                          className="w-full mt-1 h-9 px-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 focus:border-[#1976D2]"
                        />
                      </div>
                      <div className="flex justify-end gap-2 pt-1">
                        <button data-testid={`edit-cancel-${n.note_id}`} onClick={cancelEdit} className="px-3 h-8 rounded-lg text-slate-500 hover:bg-slate-100 text-sm font-medium">Cancel</button>
                        <button data-testid={`edit-save-${n.note_id}`} onClick={() => saveEdit(n.note_id)} className="px-3 h-8 rounded-lg border border-slate-300 hover:bg-white text-sm font-medium flex items-center gap-1.5"><Save className="w-3.5 h-3.5" /> Save draft</button>
                        <button data-testid={`edit-sign-${n.note_id}`} onClick={() => signNote(n.note_id)} className="px-3 h-8 rounded-lg bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-sm font-semibold flex items-center gap-1.5"><Signature className="w-3.5 h-3.5" /> Sign & lock</button>
                      </div>
                    </div>
                  )}
                  {!isEditing && (
                    <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                      {hasBody && (
                        <button data-testid={`toggle-note-${n.note_id}`} onClick={() => setExpanded(isOpen ? null : n.note_id)} className="text-[11px] text-[#1565C0] font-medium hover:underline">
                          {isOpen ? "Show less" : "Show full note"}
                        </button>
                      )}
                      {!n.signed && (
                        <>
                          <button data-testid={`draft-edit-${n.note_id}`} onClick={() => startEdit(n)} className="text-[11px] text-[#1565C0] font-medium hover:underline">Edit</button>
                          <button data-testid={`draft-sign-${n.note_id}`} onClick={() => signNote(n.note_id)} className="text-[11px] text-[#1B5E20] font-semibold hover:underline">Sign & lock</button>
                        </>
                      )}
                    </div>
                  )}
                {(n.amendments||[]).length>0 && (
                  <div className="mt-2 text-xs bg-slate-50 border-l-2 border-slate-300 p-2">
                    <div className="font-semibold text-slate-600 mb-1">Amendments</div>
                    {(n.amendments||[]).map((a,i)=><div key={i}>· {a.text} <span className="text-slate-400">— {a.by} · {new Date(a.at).toLocaleString()}</span></div>)}
                  </div>
                )}
                {n.signed && <button data-testid={`amend-${n.note_id}`} onClick={async ()=>{ const t = window.prompt("Addendum text:"); if (t) { await api.post("/soap/amend", { note_id: n.note_id, addendum: t }); toast.success("Amendment added"); onReload(); }}} className="mt-2 text-xs text-[#1565C0] hover:underline">+ Add addendum</button>}
              </li>
              );
            })}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}

function VisitsTab({ visits }) {
  const [openId, setOpenId] = React.useState(null);

  if (!visits || visits.length === 0) {
    return (
      <SectionCard title="Previous visits" testId="visits-tab">
        <EmptyState icon={Calendar} title="No previous visits" description="Signed clinical records from past visits will be saved here as permanent Visit records tied to this patient's Patient ID." />
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Previous visits" testId="visits-tab" actions={<span className="text-xs text-slate-500">{visits.length} visit{visits.length===1?"":"s"}</span>}>
      <ol className="divide-y divide-slate-100" data-testid="visits-timeline">
        {visits.map((v) => {
          const isOpen = openId === v.visit_id;
          const dateLabel = new Date(v.visit_date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
          const counts = v.counts || {};
          const notes = v.notes || [];
          const labs = v.labs || [];
          const rx = v.rx || [];
          const imaging = v.imaging || [];
          const invoices = v.invoices || [];
          return (
            <li key={v.visit_id} className="p-4" data-testid={`visit-day-${v.visit_date}`}>
              <button onClick={() => setOpenId(isOpen ? null : v.visit_id)} className="w-full text-left flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-[#e6f0fb] text-[#1565C0] flex items-center justify-center flex-shrink-0"><Calendar className="w-4 h-4" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-sm font-semibold text-slate-900">{dateLabel} · <span className="text-slate-600 font-medium">{v.visit_type || "Consultation"}</span>{v.department ? <span className="text-slate-500 font-normal"> · {v.department}</span> : null}</div>
                    <div className="text-xs text-slate-500 tabular">{v.attending_doctor_name || "—"}</div>
                  </div>
                  <div className="text-sm text-slate-700 mt-0.5 truncate"><span className="text-slate-500">Diagnosis:</span> {v.primary_diagnosis || "—"}{v.icd_code ? <span className="text-[#1565C0] ml-2 text-xs tabular">{v.icd_code}</span> : null}</div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5 text-[11px]">
                    {counts.notes   > 0 && <Chip tone="blue">{counts.notes} SOAP</Chip>}
                    {counts.labs    > 0 && <Chip tone="amber">{counts.labs} lab{counts.labs===1?"":"s"}</Chip>}
                    {counts.rx      > 0 && <Chip tone="green">{counts.rx} Rx</Chip>}
                    {counts.imaging > 0 && <Chip tone="slate">{counts.imaging} imaging</Chip>}
                    {counts.invoices> 0 && <Chip tone="red">{counts.invoices} invoice{counts.invoices===1?"":"s"}</Chip>}
                    <span className="text-[11px] text-[#1565C0] font-medium ml-auto">{isOpen ? "Hide" : "Expand"}</span>
                  </div>
                </div>
              </button>

              {isOpen && (
                <div className="mt-3 ml-12 space-y-4" data-testid={`visit-detail-${v.visit_date}`}>
                  {notes.length > 0 && (
                    <VisitSection title="SOAP notes & diagnoses">
                      {notes.map((n) => (
                        <div key={n.note_id} className="rounded-lg border border-slate-200 p-3">
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-semibold">{n.diagnosis || "Consultation"}</div>
                            {n.signed ? <Chip tone="green">Signed</Chip> : <Chip tone="amber">Draft</Chip>}
                          </div>
                          <div className="text-xs text-slate-500">{new Date(n.created_at).toLocaleString()} · {n.author_name}</div>
                          {n.icd_code && <div className="text-xs text-[#1565C0] mt-1 tabular">ICD-10: {n.icd_code} — {n.icd_name}</div>}
                          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                            {[["Subjective", n.subjective], ["Objective", n.objective], ["Assessment", n.assessment], ["Plan", n.plan]].map(([l, val]) => val ? (
                              <div key={l}>
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{l}</div>
                                <div className="text-sm text-slate-700 whitespace-pre-wrap">{val}</div>
                              </div>
                            ) : null)}
                          </div>
                        </div>
                      ))}
                    </VisitSection>
                  )}
                  {labs.length > 0 && (
                    <VisitSection title="Lab investigations">
                      {labs.map((l) => (
                        <div key={l.order_id} className="rounded-lg border border-slate-200 p-3">
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-semibold">{l.order_no}</div>
                            <span className={`chip chip-${l.status === "Verified" ? "green" : "amber"}`}>{l.status === "Verified" ? "Ready" : l.status}</span>
                          </div>
                          <div className="text-xs text-slate-500">{(l.tests || []).join(", ")}</div>
                          {(l.results || []).length > 0 && (
                            <table className="mt-2 w-full text-xs border border-slate-200 rounded-md overflow-hidden">
                              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500"><tr><th className="text-left px-2 py-1">Test</th><th className="text-left px-2 py-1">Value</th><th className="text-left px-2 py-1">Unit</th><th className="text-left px-2 py-1">Ref</th><th className="text-left px-2 py-1">Flag</th></tr></thead>
                              <tbody>
                                {l.results.map((r, i) => (
                                  <tr key={i} className="border-t border-slate-100"><td className="px-2 py-1">{r.test}</td><td className="px-2 py-1 tabular font-semibold">{r.value}</td><td className="px-2 py-1">{r.unit}</td><td className="px-2 py-1 text-slate-500">{r.reference}</td><td className="px-2 py-1"><StatusPill status={r.flag} /></td></tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      ))}
                    </VisitSection>
                  )}
                  {imaging.length > 0 && (
                    <VisitSection title="Imaging">
                      {imaging.map((o) => (
                        <div key={o.order_id} className="rounded-lg border border-slate-200 p-3">
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-semibold">{o.imaging_type}{o.body_part ? ` · ${o.body_part}` : ""}</div>
                            <StatusPill status={o.status === "Reported" ? "Completed" : o.status} />
                          </div>
                          <div className="text-xs text-slate-500 tabular">{o.order_no} · {o.ordered_by_name}</div>
                          {o.clinical_indication && <div className="text-sm text-slate-700 mt-1">{o.clinical_indication}</div>}
                          {o.findings && <div className="text-sm text-slate-700 mt-2 whitespace-pre-wrap"><span className="text-[10px] uppercase font-semibold text-slate-500 block">Findings</span>{o.findings}</div>}
                        </div>
                      ))}
                    </VisitSection>
                  )}
                  {rx.length > 0 && (
                    <VisitSection title="Prescriptions">
                      {rx.map((r) => (
                        <div key={r.rx_id} className="rounded-lg border border-slate-200 p-3">
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-semibold">{r.rx_no}</div>
                            <StatusPill status={r.status} />
                          </div>
                          <div className="text-xs text-slate-500">{(r.items || []).map((i) => `${i.drug} ${i.dose || ""}`.trim()).join(" · ")}</div>
                        </div>
                      ))}
                    </VisitSection>
                  )}
                  {invoices.length > 0 && (
                    <VisitSection title="Billing">
                      {invoices.map((inv) => (
                        <div key={inv.invoice_id} className="rounded-lg border border-slate-200 p-3 flex items-center justify-between">
                          <div>
                            <div className="text-sm font-semibold">{inv.invoice_no}</div>
                            <div className="text-xs text-slate-500 tabular">{new Date(inv.created_at).toLocaleString()}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-semibold tabular">{UGX(inv.total || 0)}</div>
                            <StatusPill status={inv.status} />
                          </div>
                        </div>
                      ))}
                    </VisitSection>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </SectionCard>
  );
}

function VisitSection({ title, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function OrderSetModal({ patient, onClose, onDone }) {
  const [sets, setSets] = useState([]);
  const [applying, setApplying] = useState(null);
  const [previewCode, setPreviewCode] = useState(null);
  const [preview, setPreview] = useState(null); // {labs, meds, total, ...}
  const [linePrices, setLinePrices] = useState({}); // { [name]: overriddenPrice }
  const [totalMode, setTotalMode] = useState("auto");
  const [customTotal, setCustomTotal] = useState(0);

  useEffect(()=>{ api.get("/order-sets").then(r=>setSets(r.data||[])); }, []);
  useEffect(() => {
    if (!previewCode) { setPreview(null); return; }
    setPreview(null); setLinePrices({});
    api.get(`/order-sets/preview?code=${encodeURIComponent(previewCode)}`).then((r) => {
      setPreview(r.data);
      setCustomTotal(r.data.total);
      setTotalMode("auto");
    }).catch(() => toast.error("Could not preview costs"));
  }, [previewCode]);

  // Recompute total from possibly-overridden line prices
  const effectiveLineTotal = preview ? (
    (preview.labs.reduce((s, x) => s + (Number(linePrices[x.description] ?? x.unit_price) || 0), 0)) +
    (preview.meds.reduce((s, x) => s + x.qty * (Number(linePrices[x.description] ?? x.unit_price) || 0), 0))
  ) : 0;
  const effectiveTotal = totalMode === "override" ? Math.max(0, Number(customTotal) || 0) : effectiveLineTotal;
  const hasLineOverrides = Object.keys(linePrices).length > 0;

  const setLinePrice = (name, val) => {
    const n = val === "" ? "" : Math.max(0, Number(val) || 0);
    setLinePrices((prev) => ({ ...prev, [name]: n }));
  };

  const confirm = async () => {
    if (!previewCode) return;
    let allowZero = false;
    if (effectiveTotal <= 0) {
      if (!window.confirm("No charge entered for this order set — continue without billing?")) return;
      allowZero = true;
    }
    // Send full line_prices map so backend records per-item overrides for audit
    const line_prices = {};
    if (hasLineOverrides) {
      Object.entries(linePrices).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined) line_prices[k] = Number(v); });
    }
    setApplying(previewCode);
    try {
      const { data } = await api.post("/order-sets/apply", {
        patient_id: patient.patient_id,
        code: previewCode,
        line_prices: hasLineOverrides ? line_prices : null,
        total_override: totalMode === "override" ? Number(effectiveTotal) : null,
        allow_zero: allowZero,
      });
      const inv = data?.invoice;
      toast.success(inv ? `${data.order_set} applied · billed ${UGX(inv.total)} — ${inv.invoice_no}` : `${data.order_set} applied`);
      onDone();
    } catch { toast.error("Failed"); } finally { setApplying(null); }
  };

  const priceRow = (x, key, isMed = false) => {
    const overridden = linePrices[x.description] !== undefined && linePrices[x.description] !== "";
    const price = overridden ? linePrices[x.description] : x.unit_price;
    const showMissing = x.unit_price === null || x.unit_price === undefined;
    const label = showMissing ? "No fee on file" : (x.source || "Standard fee schedule");
    const date = x.updated_at ? new Date(x.updated_at).toLocaleDateString() : null;
    return (
      <tr key={key} data-testid={`os-line-${key}`}>
        <td className="px-3 py-2">
          <span className={`chip ${isMed ? "chip-amber" : "chip-green"} text-[10px] mr-2`}>{isMed ? "Pharmacy" : "Lab"}</span>
          {x.description}{isMed ? ` × ${x.qty}` : ""}
          <div className="text-[10px] text-slate-500 mt-0.5">{label}{date ? ` · updated ${date}` : ""}</div>
        </td>
        <td className="px-3 py-2 text-right">
          <input
            data-testid={`os-line-input-${key}`}
            type="number" min={0} step={100}
            value={price ?? ""}
            placeholder={showMissing ? "enter" : ""}
            onChange={(e) => setLinePrice(x.description, e.target.value)}
            className={`w-28 h-8 px-2 rounded-md border text-right tabular text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 ${overridden ? "border-[#1976D2] bg-[#f0f7ff] font-semibold" : "border-slate-200"} ${showMissing ? "border-amber-400 bg-amber-50" : ""}`}
          />
          <div className="text-[10px] text-slate-500 mt-0.5">× {x.qty} = <span className="font-semibold tabular">{UGX(x.qty * (price || 0))}</span></div>
        </td>
      </tr>
    );
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-2xl w-full p-6 max-h-[92vh] overflow-y-auto shadow-xl" onClick={e=>e.stopPropagation()} data-testid="order-set-modal">
        <div className="flex items-center justify-between mb-4"><div className="font-semibold text-lg">Apply order set</div><button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button></div>
        {!previewCode ? (
          <div className="space-y-2">
            {sets.map(s => (
              <button key={s.code} data-testid={`os-${s.code}`} onClick={() => setPreviewCode(s.code)} className="w-full text-left p-3 rounded-lg border border-slate-200 hover:border-[#1976D2] hover:bg-[#f0f7ff]">
                <div className="flex items-center justify-between"><div className="font-medium text-sm">{s.name}</div><Chip tone="slate">{s.category}</Chip></div>
                <div className="text-xs text-slate-500 mt-1">Labs: {(s.labs||[]).join(", ") || "—"}</div>
                <div className="text-xs text-slate-500">Meds: {(s.meds||[]).map(m=>m.drug).join(", ") || "—"}</div>
              </button>
            ))}
          </div>
        ) : !preview ? (
          <div className="p-10 text-center text-sm text-slate-500">Calculating costs…</div>
        ) : (
          <div className="space-y-3" data-testid="os-preview">
            <div className="flex items-center justify-between">
              <div><div className="font-semibold text-slate-800">{preview.name}</div><div className="text-xs text-slate-500">{preview.category} · cost breakdown · tap any price to override</div></div>
              <button onClick={() => setPreviewCode(null)} className="text-xs text-slate-500 hover:text-slate-800 font-medium">← Back</button>
            </div>
            {(preview.missing_prices || []).length > 0 && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                <span className="font-semibold">No fee on file for:</span> {preview.missing_prices.join(", ")}. Enter a price below or add to the fee schedule.
              </div>
            )}
            {preview.labs.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Labs ({preview.labs.length})</div>
                <table className="w-full text-xs border border-slate-200 rounded-md overflow-hidden">
                  <tbody className="divide-y divide-slate-100">
                    {preview.labs.map((x, i) => priceRow(x, `lab-${i}`, false))}
                    <tr className="bg-slate-50"><td className="px-3 py-1.5 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Labs subtotal</td><td className="px-3 py-1.5 text-right tabular font-bold">{UGX(preview.labs.reduce((s, x) => s + (Number(linePrices[x.description] ?? x.unit_price) || 0), 0))}</td></tr>
                  </tbody>
                </table>
              </div>
            )}
            {preview.meds.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Medications ({preview.meds.length})</div>
                <table className="w-full text-xs border border-slate-200 rounded-md overflow-hidden">
                  <tbody className="divide-y divide-slate-100">
                    {preview.meds.map((x, i) => priceRow(x, `med-${i}`, true))}
                    <tr className="bg-slate-50"><td className="px-3 py-1.5 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Meds subtotal</td><td className="px-3 py-1.5 text-right tabular font-bold">{UGX(preview.meds.reduce((s, x) => s + x.qty * (Number(linePrices[x.description] ?? x.unit_price) || 0), 0))}</td></tr>
                  </tbody>
                </table>
              </div>
            )}
            <div className="rounded-lg border border-[#e3ebf5] bg-[#f6f9ff] p-3 space-y-1.5">
              <div className="text-[11px] uppercase tracking-wider text-[#1565C0] font-semibold">Billing total</div>
              <label className="flex items-center gap-1.5 text-sm">
                <input type="radio" data-testid="os-total-auto" checked={totalMode === "auto"} onChange={() => setTotalMode("auto")} className="accent-[#1976D2]" />
                Bill from line items {hasLineOverrides && <span className="text-[10px] text-[#1565C0] font-semibold">· {Object.keys(linePrices).length} line override(s)</span>} <span className="tabular font-semibold ml-auto">{UGX(effectiveLineTotal)}</span>
              </label>
              <label className="flex items-center gap-1.5 text-sm">
                <input type="radio" data-testid="os-total-override" checked={totalMode === "override"} onChange={() => { setTotalMode("override"); setCustomTotal(effectiveLineTotal); }} className="accent-[#1976D2]" />
                Override total <span className="text-[10px] text-amber-700">· bypasses itemized breakdown</span>
                <input data-testid="os-total-custom" type="number" min={0} step={500} disabled={totalMode !== "override"} value={customTotal} onChange={(e) => setCustomTotal(e.target.value)} className="ml-auto w-32 h-8 px-2 rounded border border-slate-200 text-right tabular text-sm disabled:bg-slate-100" />
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-300 text-sm font-medium">Cancel</button>
              <button data-testid="os-confirm" onClick={confirm} disabled={applying===previewCode} className="h-10 px-5 rounded-lg bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-sm font-semibold disabled:opacity-50">Apply & bill {UGX(effectiveTotal)}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AdmitModal({ patient, onClose, onDone }) {
  const [beds, setBeds] = useState([]);
  // admission_charge is ALWAYS blank on open — never pre-filled from ward, diagnosis, prior admits, or any other source
  const [f, setF] = useState({ ward: "", bed_no: "", reason: "", admitting_diagnosis: "", expected_los_days: 3, admission_charge: "" });

  useEffect(() => {
    api.get("/beds").then((r) => {
      const items = r.data?.beds || [];
      setBeds(items);
      // Auto-pick the first ward that has at least one vacant bed
      const firstFree = items.find((b) => (b.status || "vacant") === "vacant");
      if (firstFree) setF((prev) => ({ ...prev, ward: firstFree.ward }));
    }).catch(() => toast.error("Failed to load bed board"));
  }, []); // eslint-disable-line

  // Derive wards + available beds per ward from the live bed board
  const wards = Array.from(new Set(beds.map((b) => b.ward))).sort();
  const bedsForWard = beds.filter((b) => b.ward === f.ward);
  const freeBeds = bedsForWard.filter((b) => (b.status || "vacant") === "vacant");
  const wardCount = (w) => {
    const inWard = beds.filter((b) => b.ward === w);
    const free = inWard.filter((b) => (b.status || "vacant") === "vacant").length;
    return { total: inWard.length, free };
  };

  const submit = async () => {
    if (!f.ward) return toast.error("Pick a ward");
    if (!f.bed_no) return toast.error("Pick a bed");
    if (!f.reason || !f.admitting_diagnosis) return toast.error("Reason and admitting diagnosis are required");
    const charge = Number(f.admission_charge);
    if (!f.admission_charge || isNaN(charge) || charge <= 0) {
      return toast.error("Enter a positive admission charge — this is required before admitting.");
    }
    try {
      const { data } = await api.post("/admissions", {
        patient_id: patient.patient_id,
        ...f,
        admission_charge: charge,
      });
      toast.success(`Admitted to ${f.ward} · Bed ${f.bed_no} · ${UGX(charge)} billed on ${data.invoice_no}`);
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-md w-full p-6" onClick={e=>e.stopPropagation()} data-testid="admit-modal">
        <div className="font-semibold mb-3">Admit {patient.first_name} {patient.last_name}</div>
        <div className="space-y-3">
          <label className="block"><span className="text-[11px] uppercase text-slate-500 font-semibold">Ward *</span>
            <select data-testid="adm-ward" value={f.ward} onChange={e=>setF({...f, ward: e.target.value, bed_no: ""})} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
              {wards.length === 0 && <option value="">Loading…</option>}
              {wards.map((w) => {
                const c = wardCount(w);
                return <option key={w} value={w} disabled={c.free === 0}>{w} · {c.free} of {c.total} free{c.free === 0 ? " (full)" : ""}</option>;
              })}
            </select>
          </label>
          <label className="block"><span className="text-[11px] uppercase text-slate-500 font-semibold">Bed *</span>
            <select data-testid="adm-bed" value={f.bed_no} onChange={e=>setF({...f, bed_no: e.target.value})} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white disabled:bg-slate-100" disabled={!f.ward || freeBeds.length === 0}>
              <option value="">{!f.ward ? "Pick a ward first" : freeBeds.length === 0 ? "No free beds in this ward" : `— select from ${freeBeds.length} free beds —`}</option>
              {freeBeds.map((b) => <option key={b.bed_id} value={b.bed_no}>{b.bed_no}</option>)}
            </select>
            {f.ward && freeBeds.length > 0 && (
              <div className="text-[11px] text-slate-500 mt-1">Selecting a bed marks it Occupied on the Bed Board immediately.</div>
            )}
          </label>
          <label className="block"><span className="text-[11px] uppercase text-slate-500 font-semibold">Reason for admission *</span>
            <input data-testid="adm-reason" value={f.reason} onChange={e=>setF({...f,reason:e.target.value})} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          </label>
          <label className="block"><span className="text-[11px] uppercase text-slate-500 font-semibold">Admitting diagnosis *</span>
            <input data-testid="adm-diagnosis" value={f.admitting_diagnosis} onChange={e=>setF({...f,admitting_diagnosis:e.target.value})} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          </label>
          <label className="block"><span className="text-[11px] uppercase text-slate-500 font-semibold">Expected LOS (days)</span>
            <input type="number" value={f.expected_los_days} onChange={e=>setF({...f,expected_los_days:Number(e.target.value)})} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm tabular" />
          </label>
          <label className="block"><span className="text-[11px] uppercase text-slate-500 font-semibold">Admission charge (UGX) *</span>
            <input
              data-testid="adm-charge"
              type="number"
              min={0}
              step="any"
              value={f.admission_charge}
              onChange={(e) => setF({ ...f, admission_charge: e.target.value })}
              placeholder="Enter amount (no default)"
              className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm tabular text-right focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40"
              required
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Enter the admission fee for this patient. This amount will be posted directly to the patient's billing account on admission.
            </div>
          </label>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 h-10 rounded-lg border border-slate-300 text-sm font-medium">Cancel</button>
          <button data-testid="adm-submit" onClick={submit} disabled={!f.ward || !f.bed_no || !f.admission_charge || Number(f.admission_charge) <= 0} className="px-5 h-10 rounded-lg bg-[#2E7D32] text-white text-sm font-semibold disabled:opacity-50">Admit</button>
        </div>
      </div>
    </div>
  );
}

function BillingTab({ patient, invoices, onReload }) {
  const [show, setShow] = React.useState(false);
  const [items, setItems] = React.useState([{ description: "", qty: 1, unit_price: 0 }]);
  const [saving, setSaving] = React.useState(false);

  const total = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);
  const updateItem = (idx, field, value) => setItems((arr) => arr.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  const addLine = () => setItems((arr) => [...arr, { description: "", qty: 1, unit_price: 0 }]);
  const removeLine = (idx) => setItems((arr) => arr.length === 1 ? arr : arr.filter((_, i) => i !== idx));

  const create = async () => {
    const clean = items.filter((it) => (it.description || "").trim() && Number(it.qty) > 0 && Number(it.unit_price) >= 0)
      .map((it) => ({ description: it.description.trim(), qty: Number(it.qty), unit_price: Number(it.unit_price) }));
    if (clean.length === 0) { toast.error("Add at least one line with description, qty and price"); return; }
    setSaving(true);
    try {
      await api.post("/invoices", { patient_id: patient.patient_id, items: clean });
      toast.success(`Invoice created · UGX ${total.toLocaleString()}`);
      setShow(false);
      setItems([{ description: "", qty: 1, unit_price: 0 }]);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create invoice");
    } finally { setSaving(false); }
  };

  return (
    <>
    <SectionCard title="Billing" actions={<button data-testid="new-invoice" onClick={() => setShow(true)} className="text-sm text-[#1565C0] font-medium flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> New invoice</button>}>
      {invoices.length === 0 ? <EmptyState icon={ClipboardList} title="No invoices" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">Invoice</th>
                <th className="text-left px-4 py-2.5">Date</th>
                <th className="text-right px-4 py-2.5">Total</th>
                <th className="text-right px-4 py-2.5">Paid</th>
                <th className="text-right px-4 py-2.5">Balance</th>
                <th className="text-left px-4 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {invoices.map((i) => (
                <tr key={i.invoice_id}>
                  <td className="px-4 py-3 font-medium">{i.invoice_no}</td>
                  <td className="px-4 py-3">{new Date(i.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right tabular">{UGX(i.total)}</td>
                  <td className="px-4 py-3 text-right tabular">{UGX(i.paid || 0)}</td>
                  <td className="px-4 py-3 text-right tabular font-semibold">{UGX(i.balance ?? i.total)}</td>
                  <td className="px-4 py-3"><StatusPill status={i.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>

    {show && (
      <div className="fixed inset-0 z-40 bg-slate-900/50 flex items-center justify-center p-4" onClick={() => setShow(false)}>
        <div className="bg-white rounded-2xl border border-slate-200 max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="new-invoice-modal">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-lg font-semibold">New invoice · {patient.first_name} {patient.last_name}</div>
              <div className="text-xs text-slate-500 tabular">{patient.ths_id}</div>
            </div>
            <button onClick={() => setShow(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
          </div>

          <div className="space-y-2">
            <div className="grid grid-cols-12 gap-2 text-[11px] uppercase text-slate-500 font-semibold">
              <div className="col-span-6">Description</div>
              <div className="col-span-2 text-right">Qty</div>
              <div className="col-span-3 text-right">Unit price (UGX)</div>
              <div className="col-span-1"></div>
            </div>
            {items.map((it, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                <input data-testid={`inv-desc-${idx}`} value={it.description} onChange={(e) => updateItem(idx, "description", e.target.value)} placeholder="e.g. Consultation · Wound dressing" className="col-span-6 h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40" />
                <input data-testid={`inv-qty-${idx}`} type="number" min="1" value={it.qty} onChange={(e) => updateItem(idx, "qty", e.target.value)} className="col-span-2 h-10 px-3 rounded-lg border border-slate-200 text-sm text-right tabular" />
                <input data-testid={`inv-price-${idx}`} type="number" min="0" value={it.unit_price} onChange={(e) => updateItem(idx, "unit_price", e.target.value)} className="col-span-3 h-10 px-3 rounded-lg border border-slate-200 text-sm text-right tabular" />
                <button data-testid={`inv-remove-${idx}`} onClick={() => removeLine(idx)} disabled={items.length === 1} className="col-span-1 text-slate-400 hover:text-red-600 disabled:opacity-30 flex justify-center"><X className="w-4 h-4" /></button>
              </div>
            ))}
            <button data-testid="inv-add-line" onClick={addLine} className="mt-1 text-xs text-[#1565C0] font-medium flex items-center gap-1 hover:underline"><Plus className="w-3 h-3" /> Add another line</button>
          </div>

          <div className="mt-5 pt-4 border-t border-slate-200 flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase text-slate-500 font-semibold">Total</div>
              <div className="text-xl font-bold tabular" data-testid="inv-total">{UGX(total)}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShow(false)} className="h-10 px-4 rounded-lg border border-slate-300 text-sm font-medium">Cancel</button>
              <button data-testid="inv-create-btn" onClick={create} disabled={saving} className="h-10 px-5 rounded-lg bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-sm font-semibold disabled:opacity-50">
                {saving ? "Creating…" : "Create invoice"}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

function MARTab({ patient }) {
  const [rx, setRx] = useState([]);
  const [mar, setMar] = useState([]);
  const [showAdd, setShowAdd] = useState(null);

  const load = async () => {
    const [{ data: r }, { data: m }] = await Promise.all([
      api.get(`/prescriptions?patient_id=${patient.patient_id}`),
      api.get(`/mar?patient_id=${patient.patient_id}`),
    ]);
    setRx(r || []); setMar(m || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patient.patient_id]);

  const items = rx.flatMap((r) => (r.items || []).map((it, i) => ({ rx_id: r.rx_id, rx_no: r.rx_no, ...it, index: i, status: r.status })));

  return (
    <SectionCard title="Medication Administration Record (MAR)">
      {items.length === 0 ? <EmptyState icon={Pill} title="No active prescriptions" description="MAR entries will appear here once medications are prescribed." /> : (
        <div className="p-5 space-y-4">
          <div className="grid gap-2">
            {items.map((it, idx) => (
              <div key={idx} className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3" data-testid={`mar-row-${idx}`}>
                <div>
                  <div className="text-sm font-medium">{it.drug}</div>
                  <div className="text-xs text-slate-500">{it.dose} · {it.frequency} · {it.duration} · Qty {it.qty} · {it.rx_no}</div>
                </div>
                <button data-testid={`mar-add-${idx}`} onClick={() => setShowAdd(it)} className="h-8 px-3 rounded-lg bg-[#1976D2] text-white text-xs font-semibold hover:bg-[#1565C0]">Record dose</button>
              </div>
            ))}
          </div>
          <div>
            <div className="text-xs uppercase text-slate-500 font-semibold mb-2">Recent administrations</div>
            {mar.length === 0 ? <div className="text-sm text-slate-500 italic">No records yet.</div> : (
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
                {mar.map((m) => (
                  <li key={m.mar_id} className="p-3 flex items-center justify-between" data-testid={`mar-entry-${m.mar_id}`}>
                    <div>
                      <div className="text-sm font-medium">{m.drug} · {m.dose}</div>
                      <div className="text-xs text-slate-500">{m.recorded_by_name} · {new Date(m.created_at).toLocaleString()}</div>
                      {m.notes && <div className="text-xs text-slate-500 mt-0.5">{m.notes}</div>}
                    </div>
                    <StatusPill status={m.action === "Administered" ? "Verified" : m.action === "Held" ? "Pending" : "Rejected"} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
      {showAdd && <AddMARModal patient={patient} item={showAdd} onClose={() => setShowAdd(null)} onDone={() => { setShowAdd(null); load(); }} />}
    </SectionCard>
  );
}

function AddMARModal({ patient, item, onClose, onDone }) {
  const [action, setAction] = useState("Administered");
  const [site, setSite] = useState("");
  const [notes, setNotes] = useState("");
  const submit = async () => {
    try {
      await api.post("/mar", { rx_id: item.rx_id, patient_id: patient.patient_id, drug: item.drug, dose: item.dose, action, site, notes });
      toast.success("MAR entry recorded");
      onDone();
    } catch { toast.error("Failed to save"); }
  };
  return (
    <div className="fixed inset-0 z-40 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="mar-modal">
        <div className="flex items-center justify-between mb-3"><div className="font-semibold">Record medication</div><button onClick={onClose} className="text-slate-400"><X className="w-4 h-4" /></button></div>
        <div className="text-sm mb-3"><strong>{item.drug}</strong> · {item.dose}</div>
        <label className="block mb-3"><span className="text-[11px] uppercase text-slate-500 font-semibold">Action *</span>
          <select data-testid="mar-action" value={action} onChange={(e) => setAction(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
            <option>Administered</option><option>Refused</option><option>Held</option><option>Missed</option>
          </select>
        </label>
        <label className="block mb-3"><span className="text-[11px] uppercase text-slate-500 font-semibold">Site (if injection)</span>
          <input data-testid="mar-site" value={site} onChange={(e) => setSite(e.target.value)} placeholder="e.g. Left deltoid" className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        </label>
        <label className="block mb-4"><span className="text-[11px] uppercase text-slate-500 font-semibold">Notes</span>
          <textarea data-testid="mar-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        </label>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-300 text-sm font-medium">Cancel</button>
          <button data-testid="mar-submit" onClick={submit} className="h-10 px-4 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold">Save</button>
        </div>
      </div>
    </div>
  );
}

function IOTab({ patient }) {
  const [data, setData] = useState({ items: [], totals: { intake_ml: 0, output_ml: 0 } });
  const [kind, setKind] = useState("Intake");
  const [cat, setCat] = useState("Oral");
  const [vol, setVol] = useState(200);
  const [notes, setNotes] = useState("");

  const load = async () => {
    const { data: d } = await api.get(`/io?patient_id=${patient.patient_id}`);
    setData(d || { items: [], totals: { intake_ml: 0, output_ml: 0 } });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patient.patient_id]);

  const submit = async () => {
    if (!(Number(vol) > 0)) { toast.error("Volume must be > 0"); return; }
    try {
      await api.post("/io", { patient_id: patient.patient_id, kind, category: cat, volume_ml: Number(vol), notes });
      toast.success(`${kind} ${vol} mL recorded`);
      setNotes(""); setVol(200);
      load();
    } catch { toast.error("Failed"); }
  };

  const balance = data.totals.intake_ml - data.totals.output_ml;
  return (
    <SectionCard title="Intake & output">
      <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-lg border border-slate-200 p-4 text-center">
          <div className="text-[11px] uppercase text-slate-500 font-semibold">Intake</div>
          <div className="text-2xl font-bold tabular text-[#1565C0]" data-testid="io-intake-total">{data.totals.intake_ml} mL</div>
        </div>
        <div className="rounded-lg border border-slate-200 p-4 text-center">
          <div className="text-[11px] uppercase text-slate-500 font-semibold">Output</div>
          <div className="text-2xl font-bold tabular text-[#B71C1C]" data-testid="io-output-total">{data.totals.output_ml} mL</div>
        </div>
        <div className={`rounded-lg border p-4 text-center ${balance >= 0 ? "bg-[#eef8f0] border-[#c9e5d1]" : "bg-[#fdecec] border-[#f5c2c2]"}`}>
          <div className="text-[11px] uppercase text-slate-500 font-semibold">Balance</div>
          <div className={`text-2xl font-bold tabular ${balance >= 0 ? "text-[#1B5E20]" : "text-[#B71C1C]"}`} data-testid="io-balance">{balance >= 0 ? "+" : ""}{balance} mL</div>
        </div>
      </div>
      <div className="p-5 border-t border-slate-200 grid grid-cols-1 lg:grid-cols-5 gap-2 items-end" data-testid="io-form">
        <label className="block col-span-1"><span className="text-[11px] uppercase text-slate-500 font-semibold">Kind</span>
          <select data-testid="io-kind" value={kind} onChange={(e) => setKind(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
            <option>Intake</option><option>Output</option>
          </select>
        </label>
        <label className="block col-span-1"><span className="text-[11px] uppercase text-slate-500 font-semibold">Category</span>
          <select data-testid="io-cat" value={cat} onChange={(e) => setCat(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
            {kind === "Intake" ? ["Oral", "IV", "Other"].map((c) => <option key={c}>{c}</option>) : ["Urine", "Vomit", "Drain", "Other"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label className="block col-span-1"><span className="text-[11px] uppercase text-slate-500 font-semibold">Volume (mL)</span>
          <input data-testid="io-vol" type="number" value={vol} onChange={(e) => setVol(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm tabular" />
        </label>
        <label className="block col-span-1"><span className="text-[11px] uppercase text-slate-500 font-semibold">Notes</span>
          <input data-testid="io-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        </label>
        <button data-testid="io-submit" onClick={submit} className="h-10 px-4 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold">Add entry</button>
      </div>
      <ul className="divide-y divide-slate-100 max-h-64 overflow-y-auto border-t border-slate-200">
        {data.items.map((e) => (
          <li key={e.io_id} className="p-3 flex items-center justify-between text-sm" data-testid={`io-row-${e.io_id}`}>
            <div>
              <span className={`chip ${e.kind === "Intake" ? "chip-blue" : "chip-red"} mr-2`}>{e.kind}</span>
              <span className="font-medium">{e.category}</span> — <span className="tabular">{e.volume_ml} mL</span>
              {e.notes && <span className="text-slate-500 ml-2">· {e.notes}</span>}
            </div>
            <div className="text-xs text-slate-500">{e.recorded_by_name} · {new Date(e.created_at).toLocaleTimeString()}</div>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}
